import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  HStack,
  VStack,
  Icon,
  Text,
  Box,
  useColorMode,
} from "@chakra-ui/react";
import {
  InfoIcon,
  WarningIcon,
  CheckCircleIcon,
  DeleteIcon,
} from "@chakra-ui/icons";

/**
 * ConfirmationModal Component
 *
 * A reusable, responsive confirmation modal with multiple variants
 *
 * Props:
 * - isOpen (bool): Controls modal visibility
 * - onClose (func): Callback when modal is closed
 * - onConfirm (func): Callback when confirm button is clicked
 * - title (string): Modal title
 * - message (string): Modal body message
 * - confirmText (string): Text for confirm button (default: "Confirm")
 * - cancelText (string): Text for cancel button (default: "Cancel")
 * - variant (string): "info" | "warning" | "success" | "danger" (default: "info")
 * - isLoading (bool): Shows loading state on confirm button
 * - isDangerous (bool): Highlights danger action styling
 * - icon (ReactNode): Custom icon to display
 * - size (string): Modal size - "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "full"
 * - closeOnConfirm (bool): Auto-close after confirm (default: true)
 * - allowOutsideClick (bool): Close on overlay click (default: false)
 */
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "info",
  isLoading = false,
  isDangerous = false,
  icon,
  size = "md",
  closeOnConfirm = true,
  allowOutsideClick = true,
}) => {
  const { colorMode } = useColorMode();

  // Variant configuration
  const variantConfig = {
    info: {
      icon: <InfoIcon boxSize={6} />,
      iconColor: "blue.500",
      accentColor: "blue",
      bgGradient:
        colorMode === "dark"
          ? "linear(to-br, blue.900, blue.800)"
          : "linear(to-br, blue.50, blue.100)",
    },
    warning: {
      icon: <WarningIcon boxSize={6} />,
      iconColor: "orange.500",
      accentColor: "orange",
      bgGradient:
        colorMode === "dark"
          ? "linear(to-br, orange.900, orange.800)"
          : "linear(to-br, orange.50, orange.100)",
    },
    success: {
      icon: <CheckCircleIcon boxSize={6} />,
      iconColor: "green.500",
      accentColor: "green",
      bgGradient:
        colorMode === "dark"
          ? "linear(to-br, green.900, green.800)"
          : "linear(to-br, green.50, green.100)",
    },
    danger: {
      icon: <DeleteIcon boxSize={6} />,
      iconColor: "red.500",
      accentColor: "red",
      bgGradient:
        colorMode === "dark"
          ? "linear(to-br, red.900, red.800)"
          : "linear(to-br, red.50, red.100)",
    },
  };

  const config = variantConfig[variant] || variantConfig.info;

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
      if (closeOnConfirm) {
        onClose();
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      isCentered
      closeOnOverlayClick={allowOutsideClick}
      closeOnEsc={true}
      motionPreset="slideInCenter"
    >
      <ModalOverlay
        bg="blackAlpha.300"
        backdropFilter="blur(4px)"
        backdropInvert="0%"
        backdropSaturate="100%"
      />
      <ModalContent boxShadow="2xl" overflow="hidden">
        {/* Decorative header bar */}
        <Box h="1" bgGradient={config.bgGradient} />

        <ModalHeader
          pt={8}
          pb={4}
          display="flex"
          alignItems="flex-start"
          gap={4}
        >
          <Box
            p={3}
            borderRadius="lg"
            bgGradient={config.bgGradient}
            color={config.iconColor}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            {icon || config.icon}
          </Box>
          <VStack align="flex-start" spacing={0} flex={1}>
            <Text
              fontSize="xl"
              fontWeight="700"
              letterSpacing="-0.5px"
              color={colorMode === "dark" ? "white" : "gray.900"}
            >
              {title}
            </Text>
          </VStack>
          <ModalCloseButton position="absolute" top={4} right={4} size="lg" />
        </ModalHeader>

        <ModalBody pb={6}>
          <Text
            color={colorMode === "dark" ? "gray.400" : "gray.600"}
            fontSize="sm"
            lineHeight={1.6}
            pl={14}
          >
            {message}
          </Text>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button
            variant="ghost"
            onClick={onClose}
            _hover={{
              bg: colorMode === "dark" ? "gray.700" : "gray.100",
            }}
          >
            {cancelText}
          </Button>
          <Button
            onClick={handleConfirm}
            isLoading={isLoading}
            loadingText="Processing..."
            colorScheme={isDangerous ? "red" : config.accentColor}
            variant={isDangerous ? "solid" : "solid"}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "md",
            }}
            transition="all 0.2s"
          >
            {confirmText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmationModal;
